/**
 * <myloros-carousel> — progressive enhancement for a scroll-snap track.
 *
 * The track scrolls natively with touch, trackpad and keyboard on its own, so
 * everything here is additive: paging arrows and a scroll-position indicator.
 * If this file never loads, the carousel still works.
 */
class MylorosCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ml-track]');
    if (!this.track) return;

    this.prevButton = this.querySelector('[data-ml-prev]');
    this.nextButton = this.querySelector('[data-ml-next]');
    this.thumb = this.querySelector('[data-ml-thumb]');

    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);

    this.prevButton?.addEventListener('click', () => this.page(-1));
    this.nextButton?.addEventListener('click', () => this.page(1));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.track);
    } else {
      window.addEventListener('resize', this.update);
    }

    this.update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('resize', this.update);
  }

  get reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Width of one card plus the gap that follows it. */
  get step() {
    const first = this.track.firstElementChild;
    if (!first) return this.track.clientWidth;
    const styles = window.getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  page(direction) {
    const step = this.step;
    if (!step) return;
    const perView = Math.max(1, Math.round(this.track.clientWidth / step));
    this.track.scrollBy({
      left: direction * step * perView,
      behavior: this.reduceMotion ? 'auto' : 'smooth'
    });
  }

  onScroll() {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const { scrollWidth, clientWidth, scrollLeft } = this.track;
    const max = scrollWidth - clientWidth;

    /* Nothing to scroll: hide the controls entirely rather than show dead ones. */
    this.toggleAttribute('data-static', max <= 1);

    const progress = max > 0 ? Math.min(1, Math.max(0, scrollLeft / max)) : 0;

    if (this.prevButton) this.prevButton.disabled = progress <= 0.001;
    if (this.nextButton) this.nextButton.disabled = progress >= 0.999;

    if (this.thumb && scrollWidth > 0) {
      const ratio = Math.min(1, clientWidth / scrollWidth);
      this.thumb.style.width = `${ratio * 100}%`;
      this.thumb.style.left = `${progress * (1 - ratio) * 100}%`;
    }
  }
}

if (!customElements.get('myloros-carousel')) {
  customElements.define('myloros-carousel', MylorosCarousel);
}
